import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  Stack,
  ListItemIcon,
  Divider,
  Collapse,
  Menu,
  MenuItem,
  Box as MuiBox
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import PhoneIcon from '@mui/icons-material/Phone';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import ArticleIcon from '@mui/icons-material/Article';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import WorkIcon from '@mui/icons-material/Work';
import GavelIcon from '@mui/icons-material/Gavel';
import EventIcon from '@mui/icons-material/Event';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CollectionsIcon from '@mui/icons-material/Collections';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { useAuth } from '../../contexts/AuthContext';

const publicPages = [
  { title: 'Home', path: '/' },
  { title: 'About Us', path: '/about' },
  { 
    title: 'Members', 
    path: null,  
    subMenu: [
      { title: 'MANAGING COMMITTEE', path: '/managing-committee' },
      { title: 'MEMBERS', path: '/members' }  
    ]
  },
  { title: 'Events', path: '/events' },
  { 
    title: 'News', 
    path: null,
    subMenu: [
      { title: 'NOTICES', path: '/news/notices' },
      { title: 'NOTIFICATIONS', path: '/news/notifications' },
      { title: 'CIRCULARS', path: '/news/circulars' }
    ]
  },
  { title: 'Gallery', path: '/gallery' },
  { title: 'Contact', path: '/contact' },
  { title: 'Career', path: '/career' },
  { title: 'Login', path: '/login' },
];

const managementMenuItems = [
  { title: 'Members', path: '/members-management', icon: <GroupIcon /> },
  { title: 'Managing Committee', path: '/managing-committee-management', icon: <ManageAccountsIcon /> },
  { title: 'Events', path: '/event-management', icon: <EventIcon /> },
  { title: 'News', path: '/news-management', icon: <ArticleIcon /> },
  { title: 'Memberships', path: '/membership-management', icon: <GroupIcon /> },
  { title: 'Careers', path: '/job-management', icon: <WorkIcon /> },
  { title: 'Court Rooms', path: '/court-room-management', icon: <GavelIcon /> },
  { title: 'Contacts', path: '/contact-management', icon: <ContactMailIcon /> },
  { title: 'Gallery', path: '/gallery-management', icon: <CollectionsIcon /> },
  { title: 'FAQs', path: '/faq-management', icon: <QuestionAnswerIcon /> },
];

const userMenuItems = [
  { title: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
  { title: 'Profile', path: '/profile', icon: <PersonIcon /> },
];

function Navigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openManagement, setOpenManagement] = useState(true);
  const [membersAnchorEl, setMembersAnchorEl] = useState(null);
  const [newsAnchorEl, setNewsAnchorEl] = useState(null);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(false);

  const isManagementPath = managementMenuItems.some(item => location.pathname === item.path);

  const isDashboard = location.pathname.startsWith('/dashboard') || 
                     location.pathname === '/profile' ||
                     isManagementPath;

  const drawerWidth = 250;

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 40;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleManagementClick = () => {
    setOpenManagement(!openManagement);
  };

  const handleMembersMenuOpen = (event) => {
    setMembersAnchorEl(event.currentTarget);
  };

  const handleMembersMenuClose = () => {
    setMembersAnchorEl(null);
  };

  const handleMembersMenuItemClick = (path) => {
    navigate(path);
    handleMembersMenuClose();
  };

  const handleMembersHover = (event) => {
    setMembersAnchorEl(event.currentTarget);
  };

  const handleMembersLeave = () => {
    setTimeout(() => {
      if (!document.querySelector('.MuiMenu-paper:hover')) {
        setMembersAnchorEl(null);
      }
    }, 100);
  };

  const handleNewsHover = (event) => {
    setNewsAnchorEl(event.currentTarget);
  };

  const handleNewsLeave = () => {
    setTimeout(() => {
      if (!document.querySelector('.MuiMenu-paper:hover')) {
        setNewsAnchorEl(null);
      }
    }, 100);
  };

  const handleNewsMenuClose = () => {
    setNewsAnchorEl(null);
  };

  const handleNewsMenuItemClick = (path) => {
    navigate(path);
    handleNewsMenuClose();
  };

  const handleMobileSubmenuToggle = (event, page) => {
    event.stopPropagation();
    if (page.title === 'Members') {
      setMembersAnchorEl(event.currentTarget);
    } else if (page.title === 'News') {
      setNewsAnchorEl(event.currentTarget);
    }
  };

  const mobileDrawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', mt: '64px' }}>
      <Box 
        sx={{ 
          py: 2, 
          px: 2,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Box
          component="img"
          src="/logo.png"
          alt="ACBA Logo"
          sx={{ 
            height: 60,
            width: 'auto'
          }}
        />
        <Typography 
          variant="h6" 
          sx={{ 
            color: '#580100',
            fontSize: '1.1rem',
            fontWeight: 600,
            textAlign: 'center',
            lineHeight: 1.2
          }}
        >
          Andheri Court's Bar Association
        </Typography>
      </Box>
      <List>
        {publicPages.map((page) => (
          page.subMenu ? (
            <Box key={page.title}>
              <ListItem
                button
                onClick={handleMobileSubmenuToggle}
                sx={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <ListItemText primary={page.title} />
                <KeyboardArrowDownIcon 
                  sx={{ 
                    transform: mobileSubmenuOpen ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.3s'
                  }} 
                />
              </ListItem>
              <Collapse in={mobileSubmenuOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {page.subMenu.map((item) => (
                    <ListItem
                      key={item.title}
                      button
                      component={Link}
                      to={item.path}
                      onClick={handleDrawerToggle}
                      sx={{
                        pl: 4,
                        '&:hover': {
                          bgcolor: 'rgba(88, 1, 0, 0.04)',
                        }
                      }}
                    >
                      <ListItemText 
                        primary={item.title} 
                        sx={{ 
                          '& .MuiTypography-root': { 
                            fontSize: '0.9rem' 
                          } 
                        }} 
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Box>
          ) : (
            <ListItem 
              key={page.title} 
              button
              component={Link} 
              to={page.path}
              sx={{
                '&:hover': {
                  bgcolor: 'rgba(88, 1, 0, 0.04)',
                }
              }}
            >
              <ListItemText primary={page.title} />
            </ListItem>
          )
        ))}
      </List>
    </Box>
  );

  const userMenu = (
    <Box sx={{ width: drawerWidth, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 3, bgcolor: '#580100' }}>
        <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
          Welcome,
        </Typography>
        <Typography variant="body1" sx={{ color: 'white', wordBreak: 'break-word' }}>
          {currentUser?.email}
        </Typography>
      </Box>
      <List sx={{ flexGrow: 1 }}>
        {userMenuItems.map((item) => (
          <ListItem 
            key={item.title} 
            component={Link} 
            to={item.path}
            sx={{
              color: '#333333',
              '&:hover': {
                bgcolor: 'rgba(88, 1, 0, 0.04)',
              },
              ...(location.pathname === item.path && {
                color: '#580100',
                bgcolor: 'rgba(88, 1, 0, 0.04)',
              })
            }}
          >
            <ListItemIcon sx={{ 
              color: location.pathname === item.path ? '#580100' : '#333333',
              minWidth: 40 
            }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.title} />
          </ListItem>
        ))}
        <ListItem 
          button 
          onClick={handleManagementClick}
          sx={{ 
            bgcolor: isManagementPath ? 'rgba(88, 1, 0, 0.08)' : 'transparent',
            '&:hover': {
              bgcolor: isManagementPath ? 'rgba(88, 1, 0, 0.12)' : 'rgba(88, 1, 0, 0.04)'
            }
          }}
        >
          <ListItemIcon sx={{ 
            color: isManagementPath ? '#580100' : '#333333',
            minWidth: 40 
          }}>
            <ManageAccountsIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Management" 
            sx={{ 
              color: isManagementPath ? '#580100' : '#333333',
              '& .MuiTypography-root': {
                fontWeight: isManagementPath ? 600 : 400
              }
            }}
          />
          {openManagement ? <KeyboardArrowDownIcon /> : <KeyboardArrowDownIcon />}
        </ListItem>
        <Collapse in={openManagement} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {managementMenuItems.map((item) => {
              const isSelected = location.pathname === item.path;
              return (
                <ListItem 
                  button 
                  component={Link} 
                  to={item.path}
                  key={item.path}
                  sx={{ 
                    pl: 4,
                    bgcolor: isSelected ? 'rgba(88, 1, 0, 0.08)' : 'transparent',
                    '&:hover': {
                      bgcolor: isSelected ? 'rgba(88, 1, 0, 0.12)' : 'rgba(88, 1, 0, 0.04)'
                    }
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: isSelected ? '#580100' : '#333333',
                    minWidth: 40 
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.title} 
                    sx={{ 
                      color: isSelected ? '#580100' : '#333333',
                      '& .MuiTypography-root': {
                        fontWeight: isSelected ? 600 : 400
                      }
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Collapse>
      </List>
      <Divider />
      <List>
        <ListItem 
          button 
          onClick={handleLogout}
          sx={{
            color: '#333333',
            '&:hover': {
              bgcolor: 'rgba(88, 1, 0, 0.04)',
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Permanent left drawer only in dashboard */}
      {currentUser && isDashboard && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              borderRight: '1px solid rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          {userMenu}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${isDashboard && currentUser ? drawerWidth : 0}px)` }
        }}
      >
        {!isDashboard && (
          <AppBar 
            position="fixed" 
            elevation={0}
            sx={{ 
              bgcolor: '#580100',
              height: 40,
              zIndex: 1201,
              width: '100%'
            }}
          >
            <Container maxWidth="xl">
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                sx={{ height: 40 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon sx={{ fontSize: 18, color: 'white' }} />
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                    CALL US TODAY • +91 83568 96595
                  </Typography>
                </Box>
              </Stack>
            </Container>
          </AppBar>
        )}

        <AppBar 
          position="fixed" 
          sx={{ 
            bgcolor: 'background.paper',
            boxShadow: scrolled ? 1 : 0,
            borderBottom: !scrolled ? '1px solid' : 'none',
            borderColor: 'divider',
            transition: 'all 0.3s ease-in-out',
            top: isDashboard ? 0 : 40,
            zIndex: 1200,
            width: '100%',
            ...(isDashboard && currentUser && {
              width: { sm: `calc(100% - ${drawerWidth}px)` },
              ml: { sm: `${drawerWidth}px` },
            })
          }}
        >
          <Container maxWidth="xl">
            <Toolbar disableGutters sx={{ minHeight: 64 }}>
              {/* Logo and Title */}
              <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: { xs: 1, md: 0 }, mr: { md: 2 } }}>
                <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                  <Box
                    component="img"
                    src="/logo.png"
                    alt="ACBA Logo"
                    sx={{
                      height: 40,
                      width: 'auto',
                      mr: 1
                    }}
                  />
                  <Typography
                    variant="h6"
                    noWrap
                    sx={{
                      display: { xs: 'none', sm: 'block' },
                      fontWeight: 700,
                      color: '#580100',
                      textDecoration: 'none',
                    }}
                  >
                    Andheri Court's Bar Association
                  </Typography>
                </Link>
              </Box>

              {/* Desktop Menu */}
              {!isDashboard && (
                <Box sx={{ 
                  display: { xs: 'none', md: 'flex' }, 
                  flexGrow: 1,
                  justifyContent: 'flex-end',
                  ml: 2,
                  height: '100%',
                }}>
                  {publicPages.map((page) => 
                    page.subMenu ? (
                      <Box 
                        key={page.title}
                        onMouseEnter={page.title === 'Members' ? handleMembersHover : handleNewsHover}
                        onMouseLeave={page.title === 'Members' ? handleMembersLeave : handleNewsLeave}
                      >
                        <Button
                          sx={{ 
                            color: '#333333',
                            mx: 0.5,
                            px: 1,
                            py: 0,
                            minHeight: '64px',
                            textTransform: 'none',
                            fontSize: '0.85rem',
                            fontWeight: location.pathname.startsWith(page.title === 'Members' ? '/members' : '/news') ? 600 : 400,
                            position: 'relative',
                            '&:hover': { 
                              bgcolor: 'rgba(88, 1, 0, 0.04)',
                            },
                            ...((page.title === 'Members' ? location.pathname.startsWith('/members') : location.pathname.startsWith('/news')) && {
                              color: '#580100',
                              bgcolor: 'rgba(88, 1, 0, 0.04)',
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: '3px',
                                backgroundColor: '#580100',
                              }
                            })
                          }}
                        >
                          {page.title}
                          <KeyboardArrowDownIcon sx={{ ml: 0.5, width: 20, height: 20 }} />
                        </Button>
                        {page.title === 'Members' ? (
                          <Menu
                            anchorEl={membersAnchorEl}
                            open={Boolean(membersAnchorEl)}
                            onClose={handleMembersMenuClose}
                            PaperProps={{
                              onMouseEnter: () => clearTimeout(),
                              onMouseLeave: handleMembersLeave,
                              sx: {
                                mt: 0.5,
                                '& .MuiMenuItem-root': {
                                  fontSize: '0.85rem',
                                  px: 2,
                                  py: 1,
                                  minHeight: 'auto',
                                  textTransform: 'uppercase',
                                  '&:hover': {
                                    bgcolor: 'rgba(88, 1, 0, 0.04)',
                                    color: '#580100',
                                  }
                                }
                              }
                            }}
                            MenuListProps={{
                              onMouseLeave: handleMembersLeave
                            }}
                          >
                            {page.subMenu.map((item) => (
                              <MenuItem 
                                key={item.title}
                                onClick={() => handleMembersMenuItemClick(item.path)}
                              >
                                {item.title}
                              </MenuItem>
                            ))}
                          </Menu>
                        ) : (
                          <Menu
                            anchorEl={newsAnchorEl}
                            open={Boolean(newsAnchorEl)}
                            onClose={handleNewsMenuClose}
                            PaperProps={{
                              onMouseEnter: () => clearTimeout(),
                              onMouseLeave: handleNewsLeave,
                              sx: {
                                mt: 0.5,
                                '& .MuiMenuItem-root': {
                                  fontSize: '0.85rem',
                                  px: 2,
                                  py: 1,
                                  minHeight: 'auto',
                                  textTransform: 'uppercase',
                                  '&:hover': {
                                    bgcolor: 'rgba(88, 1, 0, 0.04)',
                                    color: '#580100',
                                  }
                                }
                              }
                            }}
                            MenuListProps={{
                              onMouseLeave: handleNewsLeave
                            }}
                          >
                            {page.subMenu.map((item) => (
                              <MenuItem 
                                key={item.title}
                                onClick={() => handleNewsMenuItemClick(item.path)}
                              >
                                {item.title}
                              </MenuItem>
                            ))}
                          </Menu>
                        )}
                      </Box>
                    ) : (
                      (!currentUser || page.title !== 'Login') && (
                        <Button
                          key={page.title}
                          component={Link}
                          to={page.path}
                          sx={{ 
                            color: '#333333',
                            mx: 0.5,
                            px: 1,
                            py: 0,
                            minHeight: '64px',
                            textTransform: 'none',
                            fontSize: '0.85rem',
                            fontWeight: location.pathname === page.path ? 600 : 400,
                            position: 'relative',
                            '&:hover': { 
                              bgcolor: 'rgba(88, 1, 0, 0.04)',
                            },
                            ...(location.pathname === page.path && {
                              color: '#580100',
                              bgcolor: 'rgba(88, 1, 0, 0.04)',
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: '3px',
                                backgroundColor: '#580100',
                              }
                            })
                          }}
                        >
                          {page.title}
                        </Button>
                      )
                    )
                  )}
                  {currentUser && !isDashboard && (
                    <Button
                      component={Link}
                      to="/dashboard"
                      sx={{ 
                        color: '#333333',
                        mx: 0.5,
                        px: 1,
                        py: 0,
                        minHeight: '64px',
                        textTransform: 'none',
                        fontSize: '0.85rem',
                        fontWeight: location.pathname === '/dashboard' ? 600 : 400,
                        position: 'relative',
                        '&:hover': { 
                          bgcolor: 'rgba(88, 1, 0, 0.04)',
                        },
                        ...(location.pathname === '/dashboard' && {
                          color: '#580100',
                          bgcolor: 'rgba(88, 1, 0, 0.04)',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '3px',
                            backgroundColor: '#580100',
                          }
                        })
                      }}
                    >
                      Dashboard
                    </Button>
                  )}
                </Box>
              )}

              {/* Mobile Menu Icon - Right Side */}
              <Box sx={{ display: { md: 'none' } }}>
                <IconButton
                  onClick={handleDrawerToggle}
                  sx={{ 
                    color: '#580100',
                    cursor: 'pointer',
                    '&:hover': {
                      cursor: 'pointer'
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>

        {/* Mobile Navigation Drawer */}
        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: drawerWidth,
              bgcolor: 'background.default',
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
              },
            },
          }}
        >
          {mobileDrawer}
        </Drawer>

        {/* Toolbar spacer */}
        <Box sx={{ height: isDashboard ? 64 : 104 }} />
      </Box>
    </Box>
  );
}

export default Navigation;
